import { useEffect, useState, useRef } from "react";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Star } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { useAdminRatingsStore } from "@/store/api/useAdminRatingsStore";
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import * as htmlToImage from "html-to-image";
import { Dialog, DialogContent, DialogClose } from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";

export function AdminRatingsPage() {
  const { t } = useTranslation("adminui3");
  const { ratings, isLoading, fetchRatings } = useAdminRatingsStore();
  const [search, setSearch] = useState("");
  const [filterNote, setFilterNote] = useState("all");
  const [filterMember, setFilterMember] = useState("all");
  const [filterCourse, setFilterCourse] = useState("all");
  const [filterComments, setFilterComments] = useState("all");
  const [selectedRating, setSelectedRating] = useState<any | null>(null);

  const members = Array.from(
    new Map(ratings.map(r => [r.user_id, `${r.first_name} ${r.last_name}`])).entries()
  ).map(([id, name]) => ({ id, name }));

  // Les cours sans nom sont écartés : `<SelectItem value={null}>` fait lever
  // Radix (une valeur non vide est obligatoire).
  const courses = Array.from(
    new Set(ratings.map(r => r.class_name).filter((n): n is string => !!n))
  ).map(name => ({ name }));

  const filterBarClass = "flex flex-wrap gap-2 mt-4 overflow-x-auto md:overflow-visible pb-2";

  useEffect(() => {
    fetchRatings();
  }, [fetchRatings]);

  // `class_name` est nullable : sans `?.`, la moindre ligne sans nom de cours
  // faisait lever `toLowerCase` en plein rendu (page blanche à la recherche).
  const needle = search.toLowerCase();
  let filtered = ratings.filter(r =>
    (!search ||
      `${r.first_name ?? ''} ${r.last_name ?? ''}`.toLowerCase().includes(needle) ||
      (r.class_name?.toLowerCase().includes(needle) ?? false) ||
      (r.instructor?.toLowerCase().includes(needle) ?? false)) &&
    (filterNote === "all" || r.rating === Number(filterNote)) &&
    (filterMember === "all" || r.user_id === filterMember) &&
    (filterCourse === "all" || r.class_name === filterCourse) &&
    (filterComments === "all" || (filterComments === "yes" ? !!r.notes : !r.notes))
  );

  filtered = filtered.sort((a, b) => {
    // Dates nulles ou malformées rejetées en fin de liste plutôt que de
    // produire un `NaN` qui rend le tri non déterministe.
    const timeA = new Date(a.class_date).getTime();
    const timeB = new Date(b.class_date).getTime();
    if (isNaN(timeA)) return 1;
    if (isNaN(timeB)) return -1;
    return timeB - timeA;
  });

  // date-fns v3 lève sur une date invalide : garde commune à tout l'écran.
  const safeDate = (value: unknown) => {
    if (!value) return '–';
    const d = new Date(value as string);
    return isNaN(d.getTime()) ? '–' : format(d, 'dd/MM/yyyy');
  };

  // --- Modal Export ---
  const modalRef = useRef<HTMLDivElement>(null);
  const handleExportImage = async () => {
    if (!modalRef.current || !selectedRating) return;
    try {
      const dataUrl = await htmlToImage.toPng(modalRef.current, { cacheBust: true, pixelRatio: 2 });
      const base64 = dataUrl.split(',')[1];
      const fileName = `avis-${selectedRating.first_name}-${selectedRating.class_name}-${Date.now()}.png`;
  
      // Save to Filesystem
      await Filesystem.writeFile({
        path: fileName,
        data: base64,
        directory: Directory.Documents,
      });
  
      alert(t("adminRatings.imageSavedAlert"));
    } catch (e) {
      alert(t("adminRatings.saveErrorAlert"));
    }
  };
  
  const handleShareImage = async () => {
    if (!modalRef.current || !selectedRating) return;
    try {
      const dataUrl = await htmlToImage.toPng(modalRef.current, { cacheBust: true, pixelRatio: 2 });
      const base64 = dataUrl.split(',')[1];
      const fileName = `avis-${selectedRating.first_name}-${selectedRating.class_name}-${Date.now()}.png`;
  
      await Filesystem.writeFile({
        path: fileName,
        data: base64,
        directory: Directory.Documents,
      });
  
      // Use Share plugin
      await Share.share({
        title: t("adminRatings.shareTitle"),
        text: t("adminRatings.shareText"),
        url: `file://${fileName}`,
        dialogTitle: t("adminRatings.shareDialogTitle")
      });
    } catch (e) {
      alert(t("adminRatings.shareErrorAlert"));
    }
  };
  
  

  // Helper
  const renderStars = (rating: number) =>
    Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={`inline h-4 w-4 ${i < rating ? "text-yellow-400 fill-yellow-400" : "text-muted-foreground"}`} />
    ));

  return (
    <div className="max-w-6xl mx-auto p-2 sm:p-4">
      <Card>
        <CardHeader>
          <CardTitle>
            {t("adminRatings.title")} <span className="text-primary">({filtered.length})</span>
          </CardTitle>
          <div className={filterBarClass}>
            <Input
              placeholder={t("adminRatings.searchPlaceholder")}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="max-w-xs"
            />
            <Select value={filterNote} onValueChange={setFilterNote}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder={t("adminRatings.allRatings")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("adminRatings.allRatings")}</SelectItem>
                {[5,4,3,2,1].map(n => (
                    <SelectItem key={n} value={n+""}>{n} ⭐</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterMember} onValueChange={setFilterMember}>
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder={t("adminRatings.allMembers")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("adminRatings.allMembers")}</SelectItem>
                {members.map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterCourse} onValueChange={setFilterCourse}>
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder={t("adminRatings.allCourses")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("adminRatings.allCourses")}</SelectItem>
                {courses.map(c => (
                  <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterComments} onValueChange={setFilterComments}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder={t("adminRatings.allReviews")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("adminRatings.allReviews")}</SelectItem>
                <SelectItem value="yes">{t("adminRatings.withComment")}</SelectItem>
                <SelectItem value="no">{t("adminRatings.withoutComment")}</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => {
              setSearch("");
              setFilterNote("all");
              setFilterMember("all");
              setFilterCourse("all");
              setFilterComments("all");
            }}>
              {t("adminRatings.reset")}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-16 text-center text-muted-foreground">{t("adminRatings.loading")}</div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              {t("adminRatings.noReviewsFound")}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filtered.map(r => (
                <Card
                  key={r.attendance_id}
                  className="border bg-background shadow-xs cursor-pointer hover:ring-2 hover:ring-primary transition"
                  onClick={() => setSelectedRating(r)}
                  tabIndex={0}
                  role="button"
                  aria-label={t("adminRatings.viewDetailAria")}
                >
                  <CardHeader>
                    <div className="flex items-center gap-2 text-lg font-medium">
                      {renderStars(r.rating)}
                      <span className="ml-2 text-xs text-muted-foreground">
                        {r.class_name || '–'} • {safeDate(r.class_date)}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm font-semibold">{r.first_name} {r.last_name}</div>
                    <div className="text-sm text-muted-foreground">{t("adminRatings.coach", { name: r.instructor })}</div>
                    {r.notes && (
                      <div className="mt-2 px-2 py-1 rounded bg-muted text-muted-foreground italic text-xs">
                        “{r.notes}”
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      {/* === MODAL/ DIALOG Détail Avis === */}
      <Dialog open={!!selectedRating} onOpenChange={open => !open && setSelectedRating(null)}>
        <DialogContent className="max-w-md p-0 flex flex-col items-center">
          <div
            ref={modalRef}
            className="w-full max-w-xs bg-card text-card-foreground rounded-2xl shadow-xl p-5 flex flex-col items-center border-2 border-primary"
          >
            {selectedRating && (
              <>
                <div className="text-primary font-bold text-lg mb-2"></div>
                <div className="flex gap-1 items-center mb-2">{renderStars(selectedRating.rating)}</div>
                <div className="text-base font-semibold mb-1">{selectedRating.first_name}{" "}
                    <span className="inline-block blur-sm select-none">
                      {selectedRating.last_name}
                    </span>
                </div>
                <div className="text-xs text-muted-foreground mb-1">{safeDate(selectedRating.class_date)} • {selectedRating.class_name || '–'}</div>
                {selectedRating.notes && (
                  <div className="bg-muted rounded p-2 italic text-center text-sm text-muted-foreground mt-2 border">{selectedRating.notes}</div>
                )}
              </>
            )}
          </div>
          <div className="flex gap-2 mt-4 w-full max-w-xs flex-col">
            <Button variant="default" className="w-full" onClick={handleExportImage}>
              {t("adminRatings.saveImage")}
            </Button>
            <Button variant="secondary" className="w-full mt-2" onClick={handleShareImage}>
              {t("adminRatings.share")}
            </Button>
            <Button variant="outline" className="w-full mt-2" asChild>
              <DialogClose asChild>
                <span>{t("adminRatings.close")}</span>
              </DialogClose>
            </Button>
          </div>

        </DialogContent>
      </Dialog>
    </div>
  );
}
